/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import {
  Component,
  ComponentRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { IMasonryCardBase } from '@app/app/shared/directives/masonry.directive';
import { V3DialogService } from '@app/app/shared/helpers/dialog/v3-dialog.service';
import { TOAST_NOTIFICATION_SERVICE, ToastService } from '@app/app/shared/services/toast-notifications/toast-service';
import {
  LucideBookmark,
  LucideFileText,
  LucideHeart,
  LucideMessageSquare,
  LucideMoreHorizontal,
  LucidePaperclip,
  LucidePencil,
  LucideTrash,
} from '@lucide/angular';
import { TranslocoService } from '@ngneat/transloco';
import { Select, Store } from '@ngxs/store';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { AppFrameState } from '../../../../../../../app-frame/store/app-frame.state';
import { ProfileImageSummary } from '../../../../../../../shared/components/profile-image/profile-image.component';
import { V3MomentCardCommentsComponent } from '../../../../../../../shared/components/v3-moment-card-comments/v3-moment-card-comments.component';
import { EditorContent } from '../../../../../../../shared/models/editor/editor-content.model';
import { MediumEditorData } from '../../../../../../../shared/models/editor/media-content.model';
import { UserDetails } from '../../../../../../../user-auth/models';
import { UserAuthState } from '../../../../../../../user-auth/store/user-auth.state';
import { MomentCard, MomentRequest } from '../../../../../models';
import { CORE_MOMENT_DATA_SERVICE, MomentDataService } from '../../../../../shared-moment-module/services/data.service';
import { DisableFolioSectionItemVisibility, EnableFolioSectionItemVisibility } from '../../../../../store/folio.action';
import { FolioSectionItemRequest, FolioSectionItemType } from '../../../../../store/folio.state.model';
import { DeleteMoment } from '../../../../../store/moment/moment-view-all/moment-view-all.actions';

@Component({
  selector: 'ptl-v3-folio-masonry-card',
  templateUrl: './v3-folio-masonry-card.component.html',
  styleUrls: ['./v3-folio-masonry-card.component.scss'],
})
export class V3FolioMasonryCardComponent implements IMasonryCardBase, OnInit, OnDestroy {
  private _card: MomentCard;

  /** Card data. */
  @Input()
  get card(): MomentCard {
    return this._card;
  }

  set card(value: MomentCard) {
    this._card = value;
    this.previewContent = this.mergeConsecutiveParagraphs(value?.content);
  }

  /** define if moment should be archived. */
  @Input() archiveMoment: boolean;
  @Input() selected;
  @Input() uid: string;
  @Input() editMode: boolean;
  @Input() isFolioSection: boolean;
  @Input() cardVariant: 'default' | 'community' = 'default';

  @Output() cardClick = new EventEmitter<MomentCard>();
  @Output() removeCard = new EventEmitter<string>();
  @Output() likeMoment = new EventEmitter<string>();
  @Output() unlikeMoment = new EventEmitter<string>();
  @Output() saveMoment = new EventEmitter<string>();
  @Output() unsaveMoment = new EventEmitter<string>();

  @ViewChild('commentsContainer', { read: ViewContainerRef }) commentsContainerRef: ViewContainerRef;

  @Select(UserAuthState.userDetailsData)
  userDetailsData$: Observable<UserDetails>;

  @Select(AppFrameState.isMobile)
  isMobile$: Observable<boolean>;

  protected readonly LucideMessageSquare = LucideMessageSquare;
  protected readonly LucideHeart = LucideHeart;
  protected readonly LucideBookmark = LucideBookmark;
  protected readonly LucideFileText = LucideFileText;
  protected readonly LucideMoreHorizontal = LucideMoreHorizontal;
  protected readonly LucidePencil = LucidePencil;
  protected readonly LucideTrash = LucideTrash;

  commentsOpen = false;
  previewContent: EditorContent[] = [];
  currentUserId: string | null = null;
  isMenuOpen = false;
  isEditingPost = false;
  isSavingPostEdit = false;
  isDeletingPost = false;
  postEditControl = new FormControl('');
  private commentsRef: ComponentRef<V3MomentCardCommentsComponent> | null = null;
  private commentCountSub?: Subscription;
  private destroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private translocoService: TranslocoService,
    private v3DialogService: V3DialogService,
    @Inject(CORE_MOMENT_DATA_SERVICE) private momentDataService: MomentDataService,
    @Inject(TOAST_NOTIFICATION_SERVICE) private toastService: ToastService,
  ) {}

  get authorProfile(): ProfileImageSummary | undefined {
    return this.card?.author ? { firstName: this.card.author.fullName, imageUrl: this.card.author.avatarUrl } : undefined;
  }

  get isCurrentUserAuthor(): boolean {
    return !!this.currentUserId && !!this.card?.userUid && this.card.userUid === this.currentUserId;
  }

  ngOnInit(): void {
    this.userDetailsData$.pipe(takeUntil(this.destroy$)).subscribe((userDetails) => {
      this.currentUserId = userDetails?.uid || null;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.commentCountSub?.unsubscribe();
    this.commentsRef?.destroy();
    this.commentsRef = null;
  }

  /**
   * The moment editor can store one logical block of text as several consecutive
   * PARAGRAPH content items. Each item renders its own ptl-resource-html-preview,
   * which would otherwise show its own "See more" button, so merge them into one
   * paragraph before truncation is applied.
   */
  private mergeConsecutiveParagraphs(content: EditorContent[] | undefined): EditorContent[] {
    if (!content?.length) {
      return content ?? [];
    }

    const merged: EditorContent[] = [];
    for (const item of content) {
      const previous = merged[merged.length - 1];
      if (item.type === 'PARAGRAPH' && previous?.type === 'PARAGRAPH') {
        (previous as MediumEditorData).content =
          `${(previous as MediumEditorData).content || ''}${(item as MediumEditorData).content || ''}`;
      } else {
        merged.push({ ...item });
      }
    }
    return merged;
  }

  onRemoveMoment(id: string) {
    this.removeCard.emit(id);
  }

  onArchiveMoment(id: string) {
    this.store.dispatch(new DeleteMoment(id));
  }

  toggleMenu(event: Event): void {
    event.stopPropagation();
    this.isMenuOpen = !this.isMenuOpen;
  }

  closeMenu(): void {
    this.isMenuOpen = false;
  }

  onEditPostClick(event: Event): void {
    event.stopPropagation();

    const plainText = this.card.content
      .filter((item) => item.type === 'PARAGRAPH')
      .map((item) => (item as MediumEditorData).content)
      .join('')
      .replace(/<\/p><p>/g, '\n')
      .replace(/<br ?\/?>/g, '\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ');

    this.postEditControl.setValue(plainText);
    this.isEditingPost = true;
  }

  onCancelEditPost(event: Event): void {
    event.stopPropagation();
    this.isEditingPost = false;
  }

  onSaveEditPost(event: Event): void {
    event.stopPropagation();

    const trimmedText = this.postEditControl.value?.trim();
    if (!trimmedText || this.isSavingPostEdit) {
      return;
    }

    this.isSavingPostEdit = true;

    const nonTextContent = this.card.content.filter((item) => item.type !== 'PARAGRAPH');
    const textContent: MediumEditorData = {
      type: 'PARAGRAPH',
      content: this.escapeHtml(trimmedText).replace(/\n/g, '<br>'),
    } as MediumEditorData;

    const request: MomentRequest = {
      title: this.card.title || '',
      tags: this.card.tags || [],
      content: [textContent, ...nonTextContent],
      addedToFolio: this.card.momentAddedToFolio,
    };

    this.momentDataService.updateMoment(this.card._id, request).subscribe(({ isSuccess, value }) => {
      this.isSavingPostEdit = false;

      if (isSuccess) {
        this.card = { ...this.card, content: value.content };
        this.isEditingPost = false;
      } else {
        this.toastService.showFail(this.translocoService.translate('translations.errors.errorUpdatingMoment'));
      }
    });
  }

  async onDeletePostClick(event: Event): Promise<void> {
    event.stopPropagation();

    if (this.isDeletingPost) {
      return;
    }

    const confirmed = await this.v3DialogService.showConfirmDialog({
      title: this.translocoService.translate('translations.momentsList.deletePost.title'),
      confirmButtonText: this.translocoService.translate('translations.global.button.delete'),
      style: 'destructive',
    });

    if (!confirmed) {
      return;
    }

    this.isDeletingPost = true;

    this.momentDataService.deleteMoment(this.card._id).subscribe(({ isSuccess }) => {
      this.isDeletingPost = false;

      if (isSuccess) {
        this.removeCard.emit(this.card._id);
      } else {
        this.toastService.showFail(this.translocoService.translate('translations.errors.errorDeleteMoment'));
      }
    });
  }

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  onItemVisibilityState(card: MomentCard, event: Event) {
    event.stopPropagation();

    const request: FolioSectionItemRequest = {
      type: FolioSectionItemType.MOMENTS,
      uid: card._id,
    };

    if (card.visible) {
      this.store.dispatch(new DisableFolioSectionItemVisibility(this.uid, request));
    } else {
      this.store.dispatch(new EnableFolioSectionItemVisibility(this.uid, request));
    }

    card.visible = !card.visible;
  }

  onLikeMoment(event: Event) {
    event.stopPropagation();

    if (this.card.likes?.liked) {
      this.unlikeMoment.emit(this.card._id);
    } else {
      this.likeMoment.emit(this.card._id);
    }
  }

  onSaveMoment(event: Event) {
    event.stopPropagation();

    if (this.card.saved) {
      this.unsaveMoment.emit(this.card._id);
    } else {
      this.saveMoment.emit(this.card._id);
    }
  }

  toggleComments(event: Event): void {
    event.stopPropagation();

    if (this.commentsRef) {
      this.commentCountSub?.unsubscribe();
      this.commentsRef.destroy();
      this.commentsRef = null;
      this.commentsOpen = false;
      return;
    }

    this.commentsRef = this.commentsContainerRef.createComponent(V3MomentCardCommentsComponent);
    this.commentsRef.setInput('momentId', this.card._id);
    this.commentsRef.setInput('initialCommentsCount', this.card.commentsCount || 0);
    this.commentCountSub = this.commentsRef.instance.commentCountUpdate.subscribe((count: number) => {
      this.card.commentsCount = count;
    });
    this.commentsOpen = true;
  }

  onButtonClick() {
    this.cardClick.emit(this.card);
  }

  protected readonly LucidePaperclip = LucidePaperclip;
}
